import React, { useRef } from 'react';
import { isNil, pathOr } from 'ramda';

import { isMobile } from '../../utils/media';
import { PILLARS_ICONS } from './table.constants';
import {
  Header,
  HeaderColumn,
  Table,
  Pillar,
  PillarHeader,
  PillarTitle,
  PillarContent,
  Indicator,
  IndicatorHeader,
  IndicatorTitle,
  IndicatorContent,
  Dimension,
  DimensionContent,
  NumberItem,
} from './pillarTable.styles';
import { useIntl } from 'gatsby-plugin-intl';

export const PillarTable = ({ chartData }, key) => {
  const tableRef = useRef(null);
  const { fields = [], records } = chartData;

  const columnsIds = Object.values(fields).map(({ name }) => `${name[0].toLowerCase()}${name.slice(1)}`);
  const getColumn = (key) => fields[key].name;

  const getContent = (text) => {
    const match = text.match(/\d+\.(\d+)? ?/g);
    const number = pathOr('', [0], match);
    const content = text.replace(number, '');

    return { number: number.replace(/\. ?$/, ''), content };
  };

  const table = Object.values(records).reduce((list, row) => {
    const pillars = row[columnsIds[0]];
    const indicators = row[columnsIds[1]];
    const dimensions = row[columnsIds[2]];

    if (!isNil(pillars)) {
      list.push({
        content: pillars,
        children: [],
      });
    }

    const pillarId = list.length - 1;

    if (!isNil(indicators)) {
      list[pillarId].children.push({
        ...getContent(indicators),
        children: [],
      });
    }

    const indicatorId = list[pillarId].children.length - 1;

    if (!isNil(dimensions)) {
      list[pillarId].children[indicatorId].children.push({
        ...getContent(dimensions),
      });
    }

    return list;
  }, []);

  const renderHeaders = (name, key) => {
    const intl = useIntl();
    return (
      <HeaderColumn key={key} column={getColumn(key)}>
        {intl.formatMessage({ id: name.toLowerCase() }).toUpperCase()}
      </HeaderColumn>
    );
  };

  const renderDimensions = ({ content, number }, key) => {
    const intl = useIntl();
    return (
      <Dimension key={key}>
        <NumberItem>{number}</NumberItem>
        <DimensionContent>{intl.formatMessage({ id: content })}</DimensionContent>
      </Dimension>
    );
  };

  let indicatorCounter = 0;

  const renderIndicators = ({ content, number, children }, key) => {
    indicatorCounter++;

    const useColor = indicatorCounter % 2;
    const intl = useIntl();

    return (
      <Indicator key={key} useColor={useColor}>
        <IndicatorHeader>
          <NumberItem>{number}</NumberItem>
          <IndicatorTitle>{intl.formatMessage({ id: content })}</IndicatorTitle>
        </IndicatorHeader>
        <IndicatorContent>{children.map(renderDimensions)}</IndicatorContent>
      </Indicator>
    );
  };

  const handlePillarClick = (key) => () => {
    if (!isMobile()) return;

    const item = tableRef.current.querySelectorAll(PillarContent)[key];
    const height = parseInt(item.style.height, 10);

    if (height) {
      item.style.height = '0px';
    } else {
      item.style.height = `${item.scrollHeight}px`;
    }
  };

  const renderPillars = ({ content, children }, key) => {
    const Icon = PILLARS_ICONS[key];
    const intl = useIntl();
    return (
      <Pillar key={key}>
        <PillarHeader onClick={handlePillarClick(key)}>
          <Icon />
          <PillarTitle>{intl.formatMessage({ id: content })}</PillarTitle>
        </PillarHeader>
        <PillarContent>{children.map(renderIndicators)}</PillarContent>
      </Pillar>
    );
  };

  return (
    <div key={key}>
      <Header>{columnsIds.map(renderHeaders)}</Header>
      <Table ref={tableRef}>{table.map(renderPillars)}</Table>
    </div>
  );
};
